// src/stores/TestsStore.ts

import { RootStore } from "./RootStore";
import type { StateInfo } from "./QuestionsStore";
import { makeAutoObservable, runInAction } from "mobx";
import type { LoadStatus } from "./loadStatus";
import { selectInProgressTest } from "./inProgressTest";
import {
  shouldAdoptActiveTest,
  shouldDiscardActiveTest,
} from "./identityOwnership";
import { v4 as uuid } from "uuid";
import moment, { Moment } from "moment";
import * as Sentry from "@sentry/react";
import { getDeviceId } from "@/utils/identity";
import pako from "pako";
import {
  PracticeTestType,
  ElectricalUSPracticeTestType,
  SECTION_TO_SUBSECTION_IDS,
  PRACTICE_TEST_TYPE_TO_SECTION_ID,
  NUM_SECTIONS,
  APP_TO_BUILD,
} from "../../appsConfig";
import { getTests, getPracticeDays, getCodebookDays } from "@/api/fetch";
import {
  GUARANTEED_ALWAYS,
  GUARANTEED_NONE,
} from "./randomizationConstants/constants";
import { StorageKeys } from "@/utils/storage";
import * as fns from "@/api/fetch";
import {
  generateTestQuestions,
  isPassageParent,
} from "@petergok/blue-collar-shared";
import i18n from "@/i18n";
import { getQuestionIdsForTestGeneration } from "./testGenerationInputs";

// Build SUBSECTION_TO_SECTION_ID map
export const SUBSECTION_TO_SECTION_ID: Record<number, number> = {};
Object.entries(SECTION_TO_SUBSECTION_IDS).forEach(([sec, subs]) => {
  subs.forEach((sub) => {
    SUBSECTION_TO_SECTION_ID[Number(sub)] = Number(sec);
  });
});

// Fisher–Yates shuffle
export function shuffle<T>(array: T[]): T[] {
  let m = array.length;
  while (m) {
    const i = Math.floor(Math.random() * m--);
    [array[m], array[i]] = [array[i], array[m]];
  }
  return array;
}

export enum TestMode {
  PRACTICE,
  EXAM,
}

/**
 * Individual Test instance
 */
export class Test {
  testStore: TestsStore;
  id = uuid();
  testMode: TestMode;
  startTime: Moment;
  endTime?: Moment = undefined;
  isFinished = false;

  practiceTestType: any;
  canSeeAnswer: boolean;

  questions: string[];
  selectedAnswers: number[];
  submittedQuestions: boolean[];
  starredQuestions: boolean[];

  constructor(
    testStore: TestsStore,
    testMode: TestMode,
    practiceTestType: any,
    questions: string[],
    canSeeAnswer: boolean
  ) {
    this.testStore = testStore;
    this.testMode = testMode;
    this.practiceTestType = practiceTestType;
    this.canSeeAnswer = canSeeAnswer;
    this.questions = questions;
    this.selectedAnswers = new Array(questions.length).fill(0);
    this.submittedQuestions = new Array(questions.length).fill(false);
    this.starredQuestions = new Array(questions.length).fill(false);
    this.startTime = moment();

    makeAutoObservable(this, { testStore: false });
  }

  toJSON() {
    return {
      id: this.id,
      testMode: this.testMode,
      startTime: this.startTime.toISOString(),
      endTime: this.endTime?.toISOString(),
      isFinished: this.isFinished,
      practiceTestType: this.practiceTestType,
      canSeeAnswer: this.canSeeAnswer,
      questions: this.questions,
      selectedAnswers: this.selectedAnswers,
      submittedQuestions: this.submittedQuestions,
      starredQuestions: this.starredQuestions,
    };
  }

  /**
   * NOTE: runs during RootStore construction (see restoreActiveTestFromStorage), when
   * `rootStore.mainAppStore` is still undefined. Must not reach through `testStore.
   * rootStore` — doing so throws at module evaluation and white-screens the app before
   * any error boundary can mount.
   */
  static fromJSON(testStore: TestsStore, data: any): Test {
    const t = new Test(
      testStore,
      data.testMode,
      data.practiceTestType,
      data.questions,
      data.canSeeAnswer
    );
    t.id = data.id;
    t.startTime = moment(data.startTime);
    t.endTime = data.endTime ? moment(data.endTime) : undefined;
    t.isFinished = data.isFinished;
    t.selectedAnswers = data.selectedAnswers;
    t.submittedQuestions = data.submittedQuestions;
    t.starredQuestions = data.starredQuestions;
    return t;
  }

  /** Persist in-progress state */
  saveState() {
    if (this.id === this.testStore.activeTest?.id && !this.isFinished) {
      localStorage.setItem(
        StorageKeys.ACTIVE_TEST_KEY,
        JSON.stringify({
          ...this.toJSON(),
          // Stamped so a later session can prove who this exam belongs to. Storage is
          // origin-global and the sign-out sweep does not run on every logout path
          // (session expiry, Clerk's own UI, another tab), so without an owner the next
          // account to sign in silently resumes this one's attempt.
          ownerId: this.testStore.activeTestOwnerId,
        })
      );
    }
  }

  selectAnswer(questionIndex: number, answerIndex: number) {
    this.selectedAnswers[questionIndex] = answerIndex;
    this.saveState();
  }

  submitAnswer(questionIndex: number) {
    this.submittedQuestions[questionIndex] = true;
    this.saveState();
  }

  setQuestionStarred(questionIndex: number, starred: boolean) {
    this.starredQuestions[questionIndex] = starred;
    this.saveState();
  }

  submitTest() {
    this.isFinished = true;
    if (!this.endTime) this.endTime = moment();
    this.testStore.saveTestsToDevice();

    let count70 = 0;
    this.testStore.tests.forEach((test) => {
      if (test.percentCorrect() >= 0.7) count70++;
    });
    if (this.percentCorrect() >= 0.7 && (count70 === 1 || count70 === 5)) {
      setTimeout(() => {
        // showReviewFlow(); //TBD
      }, 2000);
    }

    this.testStore.activeTest = undefined;
    this.testStore.inProgressTestId = null;
    localStorage.removeItem(StorageKeys.ACTIVE_TEST_KEY);
  }

  allQuestionsLoaded(): boolean {
    const qs = this.testStore.rootStore.questionsStore.questions;
    const dqs = this.testStore.rootStore.questionsStore.deletedQuestions;
    return this.questions.every((id) => qs.has(id) || dqs.has(id));
  }

  numCorrect(sectionId?: number): number {
    let correct = 0;
    const storeQs = this.testStore.rootStore.questionsStore;
    this.questions.forEach((qid, idx) => {
      let q = storeQs.questions.get(qid) || storeQs.deletedQuestions.get(qid);
      if (!q) return;
      // Skip passage parents
      if (isPassageParent(q)) return;
      if (sectionId && q.section_id !== sectionId) return;
      const sel = this.selectedAnswers[idx];
      // Use the localization-aware scorer: prefers correct_answer_key
      // (slot identity) and only falls back to text match for legacy data.
      // Without this, Spanish answer text (or duplicate-text legacy questions)
      // mis-score against an English correct_answer field.
      if (sel != null && storeQs.isAnswerCorrect(q, sel)) {
        correct++;
      }
    });
    return correct;
  }

  numCorrectSubsection(subsectionId?: number): number {
    let correct = 0;
    const storeQs = this.testStore.rootStore.questionsStore;
    this.questions.forEach((qid, idx) => {
      let q = storeQs.questions.get(qid) || storeQs.deletedQuestions.get(qid);
      if (!q) return;
      // Skip passage parents
      if (isPassageParent(q)) return;
      if (subsectionId && Number(q.subsection_id) !== subsectionId) return;
      const sel = this.selectedAnswers[idx];
      if (sel != null && storeQs.isAnswerCorrect(q, sel)) {
        correct++;
      }
    });
    return correct;
  }

  get numAnswered(): number {
    const storeQs = this.testStore.rootStore.questionsStore;
    let count = 0;
    this.questions.forEach((qid, idx) => {
      let q = storeQs.questions.get(qid) || storeQs.deletedQuestions.get(qid);
      // Skip passage parents
      if (q && isPassageParent(q)) return;
      // `selectedAnswers` is initialised with 0 as the "unanswered" sentinel and
      // valid answer slots are 1-4, so a `!= null` check counted every
      // unanswered question as answered — inflating this to the full question
      // count and understating average answer time everywhere it is consumed.
      // Range-checking rather than switching the sentinel to null avoids a
      // migration: tests already persisted with 0s read correctly as-is.
      const selected = this.selectedAnswers[idx];
      if (typeof selected === "number" && selected >= 1 && selected <= 4) {
        count++;
      }
    });
    return count;
  }

  numQuestions(sectionId?: number): number {
    let count = 0;
    const storeQs = this.testStore.rootStore.questionsStore;
    this.questions.forEach((qid) => {
      let q = storeQs.questions.get(qid) || storeQs.deletedQuestions.get(qid);
      if (!q) return;
      // Skip passage parents
      if (isPassageParent(q)) return;
      if (sectionId && q.section_id !== sectionId) return;
      count++;
    });
    return count;
  }

  numQuestionsSubsection(subsectionId?: number): number {
    let count = 0;
    const storeQs = this.testStore.rootStore.questionsStore;
    this.questions.forEach((qid) => {
      let q = storeQs.questions.get(qid) || storeQs.deletedQuestions.get(qid);
      if (!q) return;
      // Skip passage parents
      if (isPassageParent(q)) return;
      if (subsectionId && Number(q.subsection_id) !== subsectionId) return;
      count++;
    });
    return count;
  }

  percentCorrect(sectionId?: number): number {
    const numQ = this.numQuestions(sectionId);
    if (!numQ) return 0;
    return this.numCorrect(sectionId) / numQ;
  }

  get averageAnswerTime(): number | undefined {
    if (!this.endTime || !this.numAnswered) return undefined;
    const total = this.endTime.diff(this.startTime, "seconds");
    return total / this.numAnswered;
  }

  get averageAnswerTimeString(): string {
    const time = this.averageAnswerTime;
    if (time == null) return i18n.t("common.not_applicable");
    if (time < 60) {
      return i18n.t("common.duration_seconds_short", {
        seconds: Math.round(time),
      });
    }
    const m = Math.floor(time / 60);
    const s = Math.round(time % 60);
    return i18n.t("common.duration_minutes_seconds_short", {
      minutes: m,
      seconds: s,
    });
  }

  get answerTimeGood(): boolean {
    const t = this.averageAnswerTime;
    const thresh =
      this.testStore.rootStore.questionsStore.getSecondsPerQuestion(
        this.testStore.rootStore.mainAppStore.selectedStateCode
      );
    return t != null && t <= thresh;
  }
}

const getNumQuestionsFromSplits = (
  splits: { [key: string]: { [key: string]: any } },
  numQuestions: number,
  selectedStateCode: any
): { [key: number]: number } => {
  const numPerSection: { [key: number]: number } = {};
  const sectionRemainders: { [key: number]: number } = {};
  let totalRemainder = 0;

  const allKeys: Array<number> = Array.from(Object.keys(splits)).map((key) =>
    Number(key)
  );
  for (var i = 0; i < allKeys.length; i++) {
    const splitId = allKeys[i];

    let per_of_q: any = splits[splitId.toString()]["per_of_q"];
    if (
      PracticeTestType.byState &&
      selectedStateCode &&
      typeof per_of_q === "object"
    ) {
      per_of_q = per_of_q[selectedStateCode];
    }

    const expectedQuestions = per_of_q / (1 / numQuestions);
    const guaranteedQuestions = Math.floor(expectedQuestions);
    numPerSection[splitId] = guaranteedQuestions;

    const remainder = expectedQuestions - guaranteedQuestions;
    sectionRemainders[splitId] = remainder;
    totalRemainder += remainder;
  }

  totalRemainder = Math.round(totalRemainder);

  while (totalRemainder > 0) {
    for (var i = 0; i < allKeys.length; i++) {
      const splitId = allKeys[i];
      if (Math.random() < sectionRemainders[splitId]) {
        numPerSection[splitId] += 1;
        totalRemainder -= 1;
        break;
      }
    }
  }

  return numPerSection;
};

const getNumRequiredQuestions = (
  splitValue: any,
  numQsInSection: number
): any => {
  switch (splitValue) {
    case GUARANTEED_NONE:
      return GUARANTEED_NONE;
    case GUARANTEED_ALWAYS:
      return GUARANTEED_ALWAYS;
    default:
      return Math.round(splitValue * numQsInSection);
  }
};

/**
 * Main TestsStore
 */
export default class TestsStore {
  rootStore: RootStore;
  tests = new Map<string, Test>();
  deletedTestIds = new Map<string, boolean>();
  activeTest?: Test;
  reviewAfterTest = false;
  quickPracticeDays = new Map<string, boolean>();
  codebookDays = new Map<string, boolean>();
  subsectionNames = new Map<string, string>();

  /**
   * Whether exam history has actually been fetched. An empty `tests` map means nothing
   * on its own — it is also what a pending or FAILED load looks like. Anything that
   * renders an empty state or redirects on "no tests" must check this first.
   */
  testsStatus: LoadStatus = "idle";

  /**
   * Guards `testsStatus` against writes from an abandoned request.
   *
   * `reset()` and the retry buttons can start a new load while an older `getTests()` is
   * still in flight; without this, whichever settles LAST decides the status. The flag's
   * entire job is answering "is this empty result authoritative?", so it must not be
   * writable by a request the store has already moved past.
   *
   * Non-observable (see makeAutoObservable below) — it is bookkeeping, not render state.
   */
  testsLoadSequence = 0;

  /**
   * True when a stored in-progress exam could not be parsed and was discarded. The
   * study screen surfaces this once so the student is told, rather than the exam just
   * vanishing.
   */
  activeTestRestoreFailed = false;

  /**
   * The exam that is genuinely IN PROGRESS, if any.
   *
   * Deliberately NOT derived from `activeTest`: `reviewTest()` also assigns `activeTest`
   * — with a finished test — because review reuses the /test route. So a student who is
   * mid-exam, returns to /study, and opens a past result would silently lose the resume
   * affordance while their attempt sat intact in localStorage.
   */
  inProgressTestId: string | null = null;

  /** Held for the UI to report once Sentry is initialised — see restoreActiveTestFromStorage. */
  activeTestRestoreError: unknown = null;

  /**
   * Clerk subject that owns the persisted exam, stamped into it on every save.
   *
   * Null until `enforceActiveTestOwner` runs, because `restoreActiveTestFromStorage`
   * executes during RootStore construction — at module evaluation, long before Clerk
   * has resolved a user. Ownership is therefore validated on a deferred pass rather
   * than at restore time.
   */
  activeTestOwnerId: string | null = null;

  get inProgressTest(): Test | undefined {
    // Delegates so the logic is testable — this file cannot be imported by the node test
    // runner (appsConfig pulls in image assets). See state/inProgressTest.
    return selectInProgressTest(this.tests, this.inProgressTestId);
  }

  // Single source of truth for the daily-cap key. Uses local time to match
  // the existing quickPracticeDays behavior — switching to UTC here would
  // require a one-time migration of existing keys saved in Firebase.
  getTodayKey = () => moment().format("MM/DD/YYYY");

  get hasUsedCodebookToday(): boolean {
    return this.codebookDays.has(this.getTodayKey());
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false, testsLoadSequence: false });

    // Rehydrate an in-progress exam here so /test is directly refreshable.
    this.restoreActiveTestFromStorage();

    this.loadTests();
    this.loadDeletedTestIds();
    this.loadQuickPracticeDays();
    this.loadCodebookDays();
    if (APP_TO_BUILD !== "plumbing") this.loadSubsectionNames();

    return;
  }

  async reset() {
    this.tests = new Map<string, Test>();
    this.deletedTestIds = new Map<string, boolean>();
    this.activeTest = undefined;
    this.inProgressTestId = null;
    this.reviewAfterTest = false;
    this.quickPracticeDays = new Map<string, boolean>();
    this.codebookDays = new Map<string, boolean>();
    this.subsectionNames = new Map<string, string>();
    // Back to "unknown". loadTests() below immediately sets "loading" with no render in
    // between, so this write is not what prevents an empty-state flash — it just keeps
    // the field honest if the load call is ever removed or reordered.
    this.testsStatus = "idle";

    // `reset()` clears `activeTest` but deliberately does NOT clear the stored exam, so
    // re-read it here. Restoration used to happen on every /study mount, which meant a
    // reset (e.g. the post-sign-in path in App.tsx) still recovered an in-progress exam.
    // Moving restoration to the constructor alone would have silently stranded it:
    // present on disk, unreachable in the app.
    //
    // Cross-user safety is only PARTIAL, and the honest version matters: the normal
    // logout path (useLogout) runs clearUserScopedStorage(), which removes
    // ACTIVE_TEST_KEY. But Clerk session expiry, a sign-out from Clerk's own UI, or a
    // sign-out in another tab all bypass it — so on a shared browser a stored exam can
    // outlive its owner. Pre-existing (the old /study restore did the same, and then
    // auto-navigated INTO the exam), not introduced here. The real fix is stamping a
    // user id into the payload and checking it on restore.
    this.restoreActiveTestFromStorage();

    this.loadTests();
    this.loadDeletedTestIds();
    this.loadQuickPracticeDays();
    this.loadCodebookDays();
    if (APP_TO_BUILD !== "plumbing") this.loadSubsectionNames();
  }

  startTest = (
    testMode: TestMode,
    practiceTestType: any,
    size: number = 25,
    canSeeAnswer: boolean = false,
    numPassages: number = 0,
    numRCNonPassageQuestions: number = 0
  ): Test => {
    // Override codebook year in stateInfo with effective year (user may have overridden default)
    const selectedStateCode =
      this.rootStore.mainAppStore.selectedStateCode || "";
    const effectiveYear =
      this.rootStore.questionsStore.getEffectiveCodebookYear(selectedStateCode);
    const rawStateInfo: StateInfo = this.rootStore.questionsStore.stateInfo;
    const stateInfoForTestGen =
      effectiveYear && rawStateInfo.codebookYear
        ? {
            ...rawStateInfo,
            codebookYear: {
              ...rawStateInfo.codebookYear,
              [selectedStateCode]: effectiveYear,
            },
          }
        : rawStateInfo;

    const questionIdsForTestGen = getQuestionIdsForTestGeneration({
      questions: this.rootStore.questionsStore.questions,
    });

    // Generate question IDs using the shared package
    const { questionIds: listChosenIds } = generateTestQuestions({
      testMode: testMode === TestMode.PRACTICE ? "PRACTICE" : "EXAM",
      practiceTestType,
      size,
      canSeeAnswer,
      questionIds: questionIdsForTestGen,
      questionsMap: this.rootStore.questionsStore.questions,
      questionUseFrequency: this.rootStore.questionsStore.questionUseFrequency,
      curriculum: this.rootStore.questionsStore.curriculum || {},
      typesMinMax: this.rootStore.questionsStore.typesMinMax || {},
      stateInfo: stateInfoForTestGen,
      selectedStateCode,
      appToBuild: APP_TO_BUILD,
      numSections: NUM_SECTIONS,
      practiceTestTypeToSectionId: PRACTICE_TEST_TYPE_TO_SECTION_ID,
      subsectionToSectionId: SUBSECTION_TO_SECTION_ID,
      specialTestTypes: {
        standardType: PracticeTestType.standardType,
        examType: PracticeTestType.examType,
        quickPracticeType: PracticeTestType.quickPracticeType,
        noCodebookType: PracticeTestType.noCodebookType,
        calculationOnlyType: PracticeTestType.calculationOnlyType,
        codebookMemorizationType: PracticeTestType.codebookMemorizationType,
        updates2023Type: PracticeTestType.updates2023Type,
        updates2024Type: PracticeTestType.updates2024Type,
        stateAmendmentsType: PracticeTestType.stateAmendmentsType,
      },
      practiceTestTypeByState: PracticeTestType.byState,
      getNumExamTypeQuestions: (stateCode: string) =>
        this.rootStore.questionsStore.getNumExamTypeQuestions(stateCode),
      numPassages,
      numRCNonPassageQuestions,
    });

    const newTest = new Test(
      this,
      testMode,
      practiceTestType,
      listChosenIds,
      canSeeAnswer
    );

    this.activeTest = newTest;
    this.inProgressTestId = newTest.id;
    this.tests.set(newTest.id, newTest);

    if (practiceTestType === PracticeTestType.quickPracticeType) {
      this.quickPracticeDays.set(moment().format("MM/DD/YYYY"), true);
      this.saveQuickPracticeDays();
    }

    if (practiceTestType === ElectricalUSPracticeTestType.CODEBOOK_MEMORIZATION) {
      // Only track usage for free-tier users — Pro users have unlimited
      // access, so writing the daily-usage record for them would be wasted I/O.
      const inTrial = !this.rootStore.mainAppStore.hasStudyAccess(true);
      if (inTrial) {
        this.codebookDays.set(this.getTodayKey(), true);
        this.saveCodebookDays();
      }
    }

    return newTest;
  };

  /**
   * Re-points `activeTest` at the in-progress exam and reports whether it worked.
   *
   * Necessary because `reviewTest()` shares `activeTest` with review mode: after a
   * student opens a past result, /test would otherwise render THAT instead of the
   * attempt they were resuming.
   */
  resumeInProgressTest(): boolean {
    const test = this.inProgressTest;
    if (!test) return false;
    this.activeTest = test;
    this.reviewAfterTest = false;
    return true;
  }

  /** Clears the corrupt-restore flag after the UI has surfaced it. */
  clearActiveTestRestoreFailure() {
    this.activeTestRestoreFailed = false;
    this.activeTestRestoreError = null;
  }

  /**
   * Rehydrates an in-progress exam from localStorage.
   *
   * Runs at store construction, NOT from a route component. Restoring inside the
   * `/study` screen meant `/test` could not be opened directly: refreshing mid-exam
   * mounted ExamScreen with no active test, which bounced the student to `/study`,
   * which restored and then force-navigated them back — on a two-hour exam that reads
   * as the app malfunctioning.
   *
   * A corrupt payload CLEARS the key. Leaving it meant every subsequent load retried
   * the same unparseable blob and silently failed, with the student never told their
   * in-progress exam was gone.
   */
  restoreActiveTestFromStorage() {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(StorageKeys.ACTIVE_TEST_KEY);
    } catch {
      return;
    }
    if (!raw) return;

    try {
      const test = Test.fromJSON(this, JSON.parse(raw));
      this.activeTest = test;
      this.tests.set(test.id, test);
      this.inProgressTestId = test.id;
    } catch (e) {
      // NOT reported to Sentry here: this runs during RootStore construction, which
      // happens at module evaluation — before `Sentry.init` in main.tsx. The call would
      // be silently dropped in exactly the case it was written for. The UI reports it
      // instead, once Sentry is live.
      console.error("Test could not be restored", e);
      this.activeTestRestoreError = e;
      this.activeTestRestoreFailed = true;
      try {
        localStorage.removeItem(StorageKeys.ACTIVE_TEST_KEY);
      } catch {
        /* storage unavailable — nothing further to do */
      }
    }
  }

  /**
   * Binds the restored exam to the account that is now signed in, discarding it if it
   * belongs to someone else.
   *
   * Runs from App.tsx once Clerk reports a user — it cannot run at restore time, which
   * happens during RootStore construction before any identity exists.
   *
   * An exam with no owner stamp predates this check and is ADOPTED, not discarded:
   * ownership is enforced during boot, before the student can answer, so discarding
   * would delete the work of every student mid-attempt when this ships. Cross-account
   * safety comes from the identity sweep, which clears storage before this runs. See
   * state/identityOwnership for the residual that trade accepts.
   */
  enforceActiveTestOwner(currentUserId: string | null) {
    this.activeTestOwnerId = currentUserId;
    if (!currentUserId) return;

    let stampedOwnerId: string | null = null;
    try {
      const raw = localStorage.getItem(StorageKeys.ACTIVE_TEST_KEY);
      if (!raw) return;
      stampedOwnerId = (JSON.parse(raw) as { ownerId?: string }).ownerId ?? null;
    } catch {
      // Unreadable payload: restoreActiveTestFromStorage already reports and clears it.
      return;
    }

    if (shouldAdoptActiveTest(stampedOwnerId, currentUserId)) {
      // Pre-stamp exam, same session: claim it rather than delete the student's work.
      // A real account change never reaches here — the identity sweep clears storage
      // first. See state/identityOwnership.
      this.activeTest?.saveState();
      return;
    }

    if (!shouldDiscardActiveTest(stampedOwnerId, currentUserId)) return;

    Sentry.captureMessage("active_test_discarded_foreign_owner", "warning");
    this.activeTest = undefined;
    this.inProgressTestId = null;
    try {
      localStorage.removeItem(StorageKeys.ACTIVE_TEST_KEY);
    } catch {
      /* storage unavailable — nothing further to do */
    }
  }

  discardTest(testId: string) {
    this.tests.delete(testId);
    if (this.inProgressTestId === testId) this.inProgressTestId = null;
    if (this.activeTest?.id === testId) {
      this.activeTest = undefined;
      localStorage.removeItem(StorageKeys.ACTIVE_TEST_KEY);
    }
  }

  reviewTest(testId: string, afterTest: boolean) {
    this.activeTest = this.tests.get(testId);
    this.reviewAfterTest = afterTest;
  }

  async saveQuickPracticeDays() {
    const str = JSON.stringify(Array.from(this.quickPracticeDays.entries()));
    // persist to Firestore
    try {
      await fns.setPracticeDays(str);
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  loadQuickPracticeDays() {
    getPracticeDays().then((snap) => {
      const latest = snap?.latest;
      if (latest) {
        this.quickPracticeDays = new Map(JSON.parse(latest));
      }
    });
  }

  async saveCodebookDays() {
    if (APP_TO_BUILD !== "electrical_us") return;
    const str = JSON.stringify(Array.from(this.codebookDays.entries()));
    try {
      await fns.setCodebookDays(str);
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  async loadCodebookDays() {
    if (APP_TO_BUILD !== "electrical_us") return;
    try {
      const data = await getCodebookDays();
      const latest = data?.latest;
      if (latest) {
        this.codebookDays = new Map(JSON.parse(latest));
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  async saveTestsToDevice() {
    const finished = Array.from(this.tests.values())
      .filter((t) => t.isFinished)
      .map((t) => t.toJSON());
    const str = JSON.stringify(finished);

    try {
      // Compress before sending to backend
      const compressedTests = pako.deflate(str);
      // Convert to base64 string using Bytes utility
      const base64Compressed = btoa(Array.from(compressedTests).map((b: number) => String.fromCharCode(b)).join(""));
      await fns.setTests(base64Compressed);
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  /**
   * Loads exam history.
   *
   * `testsStatus` is what stops an empty `tests` map being read as "this user has no
   * exams". Callers MUST consult it before rendering an empty state or redirecting —
   * see state/loadStatus.
   *
   * Returns the promise so callers can await it; existing call sites remain
   * fire-and-forget, which is why the status flag has to carry the information.
   */
  loadTests(): Promise<void> {
    const sequence = this.testsLoadSequence + 1;
    this.testsLoadSequence = sequence;
    // Ignore anything a superseded request reports back.
    const isCurrent = () => this.testsLoadSequence === sequence;

    this.testsStatus = "loading";

    return getTests()
      .then((snap) => {
        const result = snap?.latest;

        // A successful response with nothing in it is a real answer: this user has no
        // exams. Mark it loaded rather than returning early, or the empty state can
        // never legitimately render.
        if (!result || result.length === 0) {
          runInAction(() => {
            if (isCurrent()) this.testsStatus = "loaded";
          });
          return;
        }

        let loadedTests: any[];
        try {
          // Try to decode from base64 using Bytes utility
          const compressedBuffer = Uint8Array.from(atob(result), c => c.charCodeAt(0));
          const decompressedTests = pako.inflate(compressedBuffer, {
            to: "string",
          });
          loadedTests = JSON.parse(decompressedTests);
        } catch (decompressError) {
          // If decompression fails, try parsing as plain JSON (backward compatibility)
          try {
            loadedTests = JSON.parse(result);
          } catch (parseError) {
            console.error("Failed to parse test data:", parseError);
            Sentry.captureException(parseError);
            // Undecodable history is a FAILURE, not an empty history. Reporting it as
            // "loaded" would tell the user their exams are gone.
            runInAction(() => {
              if (isCurrent()) this.testsStatus = "error";
            });
            return;
          }
        }

        runInAction(() => {
          if (!isCurrent()) return;
          loadedTests.forEach((data: any) => {
            if (this.tests.has(data.id)) {
              return;
            }

            // Per-record, so ONE malformed row cannot fail the whole history. Without
            // this a throw escapes to the outer .catch, leaving the map partially
            // merged AND the status "error" — a student with 40 good exams and one bad
            // row would see the error screen instead of their 40 exams.
            try {
              const test = Test.fromJSON(this, data);
              this.tests.set(test.id, test);
            } catch (recordError) {
              console.error("Skipping unreadable test record", recordError);
              Sentry.captureException(recordError);
            }
          });
          this.testsStatus = "loaded";
        });
      })
      .catch((e) => {
        Sentry.captureException(e);
        runInAction(() => {
          if (isCurrent()) this.testsStatus = "error";
        });
      });
  }

  loadDeletedTestIds() {
    try {
      const cached = localStorage.getItem(StorageKeys.DELETED_TEST_IDS);
      if (cached) {
        const ids: string[] = JSON.parse(cached);
        const map = new Map<string, boolean>();
        ids.forEach((id) => map.set(id, true));
        this.deletedTestIds = map;
      }
    } catch (e) {
      Sentry.captureException(e);
    }

    fns
      .getDeletedTestIds()
      .then((data) => {
        if (data?.deletedTestIds?.length) {
          const merged = new Map(this.deletedTestIds);
          data.deletedTestIds.forEach((id) => merged.set(id, true));
          this.deletedTestIds = merged;
          localStorage.setItem(
            StorageKeys.DELETED_TEST_IDS,
            JSON.stringify(Array.from(merged.keys()))
          );
        }
      })
      .catch((e) => Sentry.captureException(e));
  }

  async softDeleteTest(testId: string) {
    // Optimistic delete — no rollback on API failure (matches mobile pattern). Errors captured in Sentry.
    this.deletedTestIds.set(testId, true);
    localStorage.setItem(
      StorageKeys.DELETED_TEST_IDS,
      JSON.stringify(Array.from(this.deletedTestIds.keys()))
    );

    try {
      await fns.deleteTest(testId);
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  deleteAllTests() {
    this.tests.clear();
    this.saveTestsToDevice();
  }

  resetQuickPracticeCount() {
    this.quickPracticeDays.clear();
    this.saveQuickPracticeDays();
  }

  resetCodebookCount() {
    this.codebookDays.clear();
    this.saveCodebookDays();
  }

  async loadSubsectionNames() {
    try {
      const { value } = await fns.getSubsectionNames();
      if (value) {
        try {
          const parsedValue = JSON.parse(value);
          const nameMap = new Map<string, string>();

          Object.keys(parsedValue).forEach((id) => {
            if (parsedValue[id]?.subsection_name) {
              nameMap.set(id, parsedValue[id].subsection_name);
            }
          });

          this.subsectionNames = nameMap;
        } catch (e) {
          Sentry.captureException(e);
        }
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  }
}
